/**
 * Reading and writing the spreadsheets a centre imports from.
 *
 * A school's staff export from Excel, Numbers or Google Sheets, which means a
 * comma or a semicolon (Excel in Spanish writes semicolons), maybe a byte-order
 * mark, quoted fields with commas inside, and headers typed by hand in Spanish
 * or English, with or without accents. All of that is read here, into records
 * keyed by a normalised header, so each import only deals with its own columns.
 */

/** One data row, keyed by normalised header, with its line in the file. */
export interface CsvRecord {
    /** The row as the spreadsheet numbers it: the header is row 1. */
    line: number;
    values: Record<string, string>;
}

export interface CsvTable {
    headers: string[];
    records: CsvRecord[];
}

/** "Nombre público " → "nombre_publico": lower case, no accents, underscores. */
export function normalizeHeader(header: string): string {
    return header
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
}

/** The separator of the header line: whichever of `;`, `,` or tab it has most of. */
function detectDelimiter(firstLine: string): string {
    const counts = [";", ",", "\t"].map((d) => [d, firstLine.split(d).length - 1] as const);
    const [best] = [...counts].sort((a, b) => b[1] - a[1]);
    return best[1] > 0 ? best[0] : ",";
}

/** Every row of the text as its fields, honouring quotes (with `""` for a quote). */
function splitRows(text: string, delimiter: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = "";
    let quoted = false;

    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];
        if (quoted) {
            if (char === '"' && text[i + 1] === '"') {
                field += '"';
                i += 1;
            } else if (char === '"') {
                quoted = false;
            } else {
                field += char;
            }
        } else if (char === '"' && field === "") {
            quoted = true;
        } else if (char === delimiter) {
            row.push(field);
            field = "";
        } else if (char === "\n" || char === "\r") {
            if (char === "\r" && text[i + 1] === "\n") i += 1;
            row.push(field);
            rows.push(row);
            row = [];
            field = "";
        } else {
            field += char;
        }
    }
    if (field !== "" || row.length > 0) {
        row.push(field);
        rows.push(row);
    }
    return rows;
}

export function parseCsv(text: string): CsvTable {
    const clean = text.replace(/^\uFEFF/, "");
    const firstLine = clean.split(/\r?\n/, 1)[0] ?? "";
    const rows = splitRows(clean, detectDelimiter(firstLine));
    if (rows.length === 0) return { headers: [], records: [] };

    const headers = rows[0].map(normalizeHeader);
    const records: CsvRecord[] = [];
    rows.slice(1).forEach((cells, offset) => {
        // A blank line — or one of only separators — is not a row.
        if (cells.every((cell) => cell.trim() === "")) return;
        const values: Record<string, string> = {};
        headers.forEach((header, column) => {
            if (header) values[header] = (cells[column] ?? "").trim();
        });
        records.push({ line: offset + 2, values });
    });
    return { headers, records };
}

/** A CSV text for these rows, quoting what needs it. Excel reads the BOM as UTF-8. */
export function toCsv(headers: string[], rows: string[][]): string {
    const quote = (value: string) => (/[",;\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
    return "\uFEFF" + [headers, ...rows].map((row) => row.map(quote).join(",")).join("\r\n") + "\r\n";
}

/** "2026-10-01", "01/10/2026" or "1-10-2026" → "2026-10-01"; null when it is not a date. */
export function parseCsvDate(raw: string): string | null {
    const value = raw.trim();
    let year: number;
    let month: number;
    let day: number;
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
    const european = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(value);
    if (iso) {
        [year, month, day] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
    } else if (european) {
        [day, month, year] = [Number(european[1]), Number(european[2]), Number(european[3])];
    } else {
        return null;
    }
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
        return null;
    }
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
